import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { KeyRound, MessageSquare } from "lucide-react";
import { Input } from "@/components/ui/input";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { api, API_BASE, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const IN_FRAME = (() => { try { return window.self !== window.top; } catch { return true; } })();

const ERRORS = {
  no_access: "Your Discord account has no staff role in any RANGER community. Ask an admin to add your Discord ID or map your Discord role.",
  invalid_state: "Login session expired or was tampered with. Try again.",
  oauth_not_configured: "Discord OAuth2 is not configured on the server.",
  token_exchange_failed: "Discord rejected the login code. Check client secret and redirect URI.",
  profile_fetch_failed: "Could not read your Discord profile.",
  discord_unreachable: "Discord could not be reached. Try again shortly.",
};

const OwnerForm = () => {
  const { setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSession(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="owner-login-form">
      <Input data-testid="login-email-input" type="email" required autoComplete="username" placeholder="Owner email" value={email} onChange={(e) => setEmail(e.target.value)} className="border-[#1E2D4A] bg-[#050811]" aria-label="Owner email" />
      <Input data-testid="login-password-input" type="password" required autoComplete="current-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="border-[#1E2D4A] bg-[#050811]" aria-label="Password" />
      {error && <p data-testid="login-error" role="alert" className="text-sm text-red-400">{error}</p>}
      <button data-testid="owner-fallback-login-button" disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-sm border border-[#1E2D4A] bg-[#111A2E] px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-slate-200 transition-colors hover:border-[#E5202E]/50 disabled:opacity-50">
        <KeyRound className="h-4 w-4" /> {busy ? "Verifying…" : "Owner sign in"}
      </button>
    </form>
  );
};

const OwnerLogin = () => {
  const [open, setOpen] = useState(false);
  if (!open) return <button type="button" data-testid="owner-login-toggle" onClick={() => setOpen(true)} className="mt-6 block w-full text-center text-xs text-slate-500 transition-colors hover:text-slate-300">Owner login</button>;
  return (
    <div className="mt-6">
      <div className="mb-3 flex items-center gap-3"><span className="h-px flex-1 bg-[#1E2D4A]" /><span className="rg-label">Owner login</span><span className="h-px flex-1 bg-[#1E2D4A]" /></div>
      <p className="mb-3 text-xs text-slate-500">For the RANGER owner account only. This is not your Discord password.</p>
      <OwnerForm />
    </div>
  );
};

export default function Login() {
  const { session, reload } = useAuth();
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  useEffect(() => { api.get("/auth/config").then((r) => setConfig(r.data)).catch(() => setConfig({})); }, []);
  useEffect(() => {
    window.addEventListener("focus", reload);
    return () => window.removeEventListener("focus", reload);
  }, [reload]);
  if (session) return <Navigate to={localStorage.getItem("ranger.returnTo") || "/overview"} replace />;
  const urlError = params.get("error");

  return (
    <div className="flex min-h-screen flex-col bg-[#050811]">
    <div className="grid flex-1 lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden lg:block">
        <img src="/login-bg.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050811]/40 to-[#050811]" />
        <div className="relative flex h-full flex-col justify-end p-14">
          <div className="rg-label text-[#E5202E]/80">Rust Console Edition · Xbox / PS5</div>
          <h2 className="mt-3 max-w-lg text-5xl font-bold uppercase leading-[0.95] tracking-wide text-slate-100">Hold the line.<br />Run the wilds.</h2>
          <p className="mt-4 max-w-md text-sm text-slate-400">Haven & Havoc · PvE with a monthly purge</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <div className="rg-fade-in w-full max-w-sm">
          <img src="/ranger-logo.jpg" alt="RANGER wolf emblem" className="h-16 w-16 rounded-sm border border-[#1E2D4A]" />
          <h1 className="mt-6 text-4xl font-bold uppercase tracking-[0.2em] text-slate-100">Ranger</h1>
          <p className="mt-2 text-sm text-slate-400">Players and staff both sign in with Discord.</p>
          <ul data-testid="login-who" className="mt-4 space-y-1.5 text-xs text-slate-400">
            <li><span className="font-semibold text-slate-200">Players:</span> see your stats, coins, rewards, battle pass, ORP, RaidLink code, teleports and shop.</li>
            <li><span className="font-semibold text-slate-200">Staff:</span> dashboard access is only given to server owners and the moderator roles they choose.</li>
          </ul>
          {urlError && <p data-testid="login-url-error" role="alert" className="mt-6 rounded-sm border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{ERRORS[urlError] || "Login failed."}</p>}
          <div className="mt-8 space-y-3">
            {config?.discord_oauth_configured ? (
              <a data-testid="discord-oauth-login-button" href={`${API_BASE}/auth/discord/login`} target={IN_FRAME ? "_blank" : "_self"} rel="noopener noreferrer" className="flex w-full items-center justify-center gap-2 rounded-sm bg-[#5865F2] px-4 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#4752c4]">
                <MessageSquare className="h-4 w-4" /> Player &amp; staff sign in with Discord
              </a>
            ) : (
              <div data-testid="discord-oauth-unconfigured" className="rounded-sm border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300">
                Discord login is not configured yet. Set DISCORD_CLIENT_ID and DISCORD_CLIENT_SECRET on the server.
              </div>
            )}
            {IN_FRAME && config?.discord_oauth_configured && <p data-testid="discord-new-tab-note" className="text-xs text-slate-500">Discord can't open inside this preview frame, so it opens in a new tab and you'll be signed in there.</p>}
          </div>
          <OwnerLogin />
          <a data-testid="login-browse-link" href="/browse" className="mt-6 block text-center text-xs text-slate-400 hover:text-white">Looking for a server? Browse the public server list →</a>
        </div>
      </div>
    </div>
    <SiteFooter />
    </div>
  );
}
